/**
 * Menu Hierarchy Handler
 *
 * This script transforms the 3rd level menu items into a custom hierarchy
 * where [HEADER] prefixed items act as category headers and subsequent items
 * are placed as 4th level items under these headers.
 */
class MenuHierarchyHandler {
  constructor() {
    this.headerDrawerSelector = 'header-drawer';
    document.addEventListener('DOMContentLoaded', this.init.bind(this));
  }

  init() {
    // Check if we're on a page with a header drawer
    if (!document.querySelector(this.headerDrawerSelector)) return;

    // Set up a mutation observer to detect when the menu drawer is opened
    const drawerContainer = document.querySelector('.menu-drawer-container');
    if (!drawerContainer) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.attributeName === 'open' &&
          drawerContainer.hasAttribute('open')
        ) {
          // Wait a small amount of time for the drawer to be fully rendered
          setTimeout(() => this.reorganizeMenu(), 50);
        }
      }
    });

    observer.observe(drawerContainer, { attributes: true });

    // Also process on page load in case the drawer is already open
    if (drawerContainer.hasAttribute('open')) {
      this.reorganizeMenu();
    }
  }

  reorganizeMenu() {
    // Process all 3rd level menus
    const thirdLevelMenus = document.querySelectorAll(
      '.menu-drawer__submenu .menu-drawer__submenu ul.menu-drawer__menu'
    );
    thirdLevelMenus.forEach((menuList) => {
      // Skip if we've already processed this menu
      if (menuList.hasAttribute('data-processed')) return;

      // Get all the list items in this menu
      const allItems = Array.from(menuList.querySelectorAll('li'));

      // If no items or already processed, skip
      if (!allItems.length) return;
      menuList.setAttribute('data-processed', 'true');

      let currentHeader = null;
      let currentHeaderItem = null;
      let itemsToMove = [];

      // First pass: identify headers and items to organize
      allItems.forEach((listItem) => {
        const link = listItem.querySelector('a');
        if (!link) return;

        const isHeader = link.hasAttribute('data-header-link');

        if (isHeader) {
          // This is a new header - process any accumulated items for the previous header
          if (currentHeader && itemsToMove.length) {
            this.createFourthLevel(
              currentHeaderItem,
              currentHeader,
              itemsToMove
            );
            itemsToMove = [];
          }

          // Set this as the new current header
          currentHeader = link;
          currentHeaderItem = listItem;

          // Convert the link to a details/summary element to act as a dropdown
          this.convertLinkToDropdown(listItem, link);
        } else {
          // This is a regular item - queue it to be moved under the current header
          if (currentHeader) {
            itemsToMove.push(listItem);
          }
        }
      });

      // Process any remaining items for the last header
      if (currentHeader && itemsToMove.length) {
        this.createFourthLevel(currentHeaderItem, currentHeader, itemsToMove);
      }
    });
  }

  convertLinkToDropdown(listItem, headerLink) {
    // Skip if already converted
    if (listItem.querySelector('details')) return;

    // Get information from the header link
    const linkId = headerLink.id;
    const linkUrl = headerLink.getAttribute('href');
    const linkTitle = headerLink.textContent.trim();
    const headerClass = headerLink.className;

    // Create the details element structure
    const details = document.createElement('details');
    details.id = `Details-${linkId}`;
    details.className = 'header-category-details';

    // Create the summary with the same styling as the original link
    const summary = document.createElement('summary');
    summary.id = linkId;
    summary.className = headerClass;
    summary.textContent = linkTitle;

    // Add the arrow icons
    const arrowWrapper1 = document.createElement('span');
    arrowWrapper1.className = 'svg-wrapper';
    arrowWrapper1.innerHTML =
      '<svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg>';

    const arrowWrapper2 = document.createElement('span');
    arrowWrapper2.className = 'svg-wrapper';
    arrowWrapper2.innerHTML =
      '<svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg>';

    summary.appendChild(arrowWrapper1);
    summary.appendChild(arrowWrapper2);

    // Create the submenu container
    const submenuDiv = document.createElement('div');
    submenuDiv.id = `category-submenu-${linkId.split('-').pop()}`;
    submenuDiv.className =
      'menu-drawer__submenu has-submenu gradient motion-reduce';

    // Create the inner structure
    const innerSubmenu = document.createElement('div');
    innerSubmenu.className = 'menu-drawer__inner-submenu';

    // Create the back button
    const backButton = document.createElement('button');
    backButton.className =
      'menu-drawer__close-button link link--text focus-inset';
    backButton.setAttribute('aria-expanded', 'true');

    const backArrow = document.createElement('span');
    backArrow.className = 'svg-wrapper';
    backArrow.innerHTML =
      '<svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg>';

    backButton.appendChild(backArrow);
    backButton.appendChild(document.createTextNode(`Back to ${linkTitle}`));

    // Create the submenu list
    const submenuList = document.createElement('ul');
    submenuList.className = 'menu-drawer__menu list-menu';
    submenuList.setAttribute('role', 'list');
    submenuList.setAttribute('tabindex', '-1');
    submenuList.dataset.parentHeader = linkId;

    // Link heading to the original URL
    if (linkUrl && linkUrl !== '#') {
      const viewAllLi = document.createElement('li');
      const viewAllLink = document.createElement('a');
      viewAllLink.href = linkUrl;
      viewAllLink.className =
        'menu-drawer__menu-item link link--text list-menu__item focus-inset';
      viewAllLink.textContent = `Explore all ${linkTitle}`;
      viewAllLi.appendChild(viewAllLink);
      submenuList.appendChild(viewAllLi);
    }

    // Assemble the structure
    innerSubmenu.appendChild(backButton);
    innerSubmenu.appendChild(submenuList);
    submenuDiv.appendChild(innerSubmenu);
    details.appendChild(summary);
    details.appendChild(submenuDiv);

    // Replace the original link with the new structure
    listItem.innerHTML = '';
    listItem.appendChild(details);

    // Set up event handler for the back button
    backButton.addEventListener('click', () => {
      details.removeAttribute('open');
    });
  }

  createFourthLevel(headerItem, headerLink, itemsToMove) {
    // Find the submenu list within the converted dropdown
    const submenuList = headerItem.querySelector('ul[data-parent-header]');
    if (!submenuList) return;

    // Move each item into the fourth level
    itemsToMove.forEach((item) => {
      const link = item.querySelector('a');
      if (!link) return;

      // Create a new list item for the fourth level
      const newItem = document.createElement('li');

      // Clone the link and add the fourth level class
      const clonedLink = link.cloneNode(true);
      clonedLink.classList.add('fourth-level-link');

      // Add the item to the fourth level submenu
      newItem.appendChild(clonedLink);
      submenuList.appendChild(newItem);

      // Hide the original item
      item.style.display = 'none';
    });
  }
}

// Initialize the menu hierarchy handler
new MenuHierarchyHandler();
